import { SocketClient } from "cbcore-ts"
import { NO, UITextView, UIView, YES } from "uicore-ts"
import { RHCore } from "../../Core/RHCore"
import { CBDataViewCellDescriptorWithObject } from "../../Custom components/CB/CBDataView"
import { CBDialogViewShower } from "../../Custom components/CB/CBDialogViewShower"
import { RHFocusManager } from "../../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHDataView } from "../../Custom components/RH/RHDataView"
import {
    RHButtons,
    RHSpacing
} from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHCompanyMembershipWorkflow } from "../../SocketClientFunctions"
import { CompanyAffiliationViewController } from "../CompanyAffiliation/CompanyAffiliationViewController"
import { RHCompanyMemberPresentation } from "./CompanyMemberPresentation"
import { CompanyRoleBadgesView } from "./CompanyRoleBadgesView"
import { JoinRequestDialogView } from "./JoinRequestDialogView"


type CompanyAdministrationWorkflowScope = "company" | "personal"
type CompanyAdministrationWorkflowAction =
    "approveOrReject" |
    "revoke" |
    "acceptOrReject" |
    "cancel" |
    "none"


type CompanyAdministrationWorkflowRow = RHCompanyMembershipWorkflow & {
    scope: CompanyAdministrationWorkflowScope
    subjectSearchText: string
    subjectText: string
    roleSearchText: string
    statusText: string
    typeText: string
    action: CompanyAdministrationWorkflowAction
}


class CompanyWorkflowActionView extends UIView {

    readonly secondaryButton = new RHButton().configuredWithObject({
        titleLabel: {
            style: {
                whiteSpace: "nowrap",
                fontWeight: RHButtons.tableActionTitleFontWeight
            }
        },
        variant: RHButtons.destructiveUtilityVariant,
        contentPadding: RHButtons.tableActionContentPadding,
        style: { fontSize: RHButtons.tableActionFontSize }
    }).addedAsSubviewToView(this)

    readonly primaryButton = new RHButton().configuredWithObject({
        titleLabel: {
            style: {
                whiteSpace: "nowrap",
                fontWeight: RHButtons.tableActionTitleFontWeight
            }
        },
        variant: RHButtons.tableActionVariant,
        contentPadding: RHButtons.tableActionContentPadding,
        style: { fontSize: RHButtons.tableActionFontSize }
    }).addedAsSubviewToView(this)

    secondaryWasPressed?: () => void
    primaryWasPressed?: () => void


    constructor(elementID?: string) {
        super(elementID)
        this.secondaryButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.secondaryWasPressed?.()
        }
        this.primaryButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.primaryWasPressed?.()
        }
        this.secondaryButton.titleLabel.textAlignment = UITextView.textAlignment.center
        this.primaryButton.titleLabel.textAlignment = UITextView.textAlignment.center
    }


    override layoutSubviews() {
        super.layoutSubviews()
        const visibleButtons = [this.secondaryButton, this.primaryButton].filter(
            button => !button.hidden
        )
        this.contentBounds
            .rectangleWithHeight(RHButtons.tableActionHeight, 0.5)
            .framesByDistributingViewsAsRow(
                visibleButtons,
                RHSpacing.small,
                visibleButtons.map(() => RHButtons.tableActionWidth),
                0.5
            )
    }


    override intrinsicContentHeight() {
        return RHButtons.tableActionHeight
    }

}


export class CompanyAdministrationJoinRequestsView extends UIView {

    readonly dataView = new RHDataView<CompanyAdministrationWorkflowRow>()
        .configuredWithObject({
            noDataMessage: "Sobivaid ootel taotlusi ega kutseid ei leitud",
            hasResizableColumns: NO,
            tableView: { allRowsHaveEqualHeight: NO },
            maximumIntrinsicContentHeight: 360
        })
        .addedAsSubviewToView(this)

    override _focusManager = new RHFocusManager(() => [
        this.dataView.searchTextField,
        this.dataView._gridContainerView
    ])

    companyId = ""
    workflowsDidChange?: () => void
    _workflows: CompanyAdministrationWorkflowRow[] = []


    constructor(elementID?: string) {
        super(elementID)
        this.dataView.searchTextField.placeholderText = "Otsi kasutaja või rolli järgi..."
        this.dataView.descriptors = this._descriptors()
    }


    updateWithWorkflowSources(
        companyWorkflows: RHCompanyMembershipWorkflow[],
        personalWorkflows: RHCompanyMembershipWorkflow[]
    ) {
        this._workflows = CompanyAdministrationJoinRequestsView._rows(
            companyWorkflows,
            personalWorkflows,
            workflow => CompanyRoleBadgesView
                .displayLabelsForRoles(workflow.requestedRoles)
                .join(", ")
        )
        this.dataView.data = this._workflows
        this.setNeedsLayout()
    }


    get workflows(): RHCompanyMembershipWorkflow[] {
        return [...this._workflows]
    }


    openWorkflowWithId(workflowId: string): boolean {
        const workflow = this._workflows.find(candidate => candidate.requestId === workflowId)
        if (!workflow || workflow.action !== "approveOrReject") {
            return NO
        }
        this._openWorkflow(workflow)
        return YES
    }


    _descriptors() {
        return [
            CBDataViewCellDescriptorWithObject<CompanyAdministrationWorkflowRow>({
                title: "Tüüp",
                keyPath: "typeText",
                allowSorting: YES,
                weight: 0.9
            }),
            CBDataViewCellDescriptorWithObject<CompanyAdministrationWorkflowRow>({
                title: "Kasutaja",
                keyPath: "subjectSearchText",
                allowSorting: YES,
                weight: 1,
                initCell: cellView => {
                    cellView.configureAsMultilineTextPreservingLineBreaks()
                    cellView.titleLabel!.configureWithObject({
                        containerStyle: { wordBreak: "break-all" },
                        style: { wordBreak: "break-all" }
                    })
                },
                updateCell: (cellView, dataItem) => {
                    cellView.text = dataItem.subjectText
                }
            }),
            CBDataViewCellDescriptorWithObject<
                CompanyAdministrationWorkflowRow,
                CompanyRoleBadgesView
            >({
                title: "Roll",
                keyPath: "roleSearchText",
                allowSorting: NO,
                weight: 1.6,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                initCustomView: () => new CompanyRoleBadgesView(),
                updateCell: (cellView, dataItem) => {
                    cellView.customView.updateWithRoles(
                        dataItem.requestedRoles,
                        dataItem.isEligibleToActAsProcurer
                    )
                }
            }),
            CBDataViewCellDescriptorWithObject<CompanyAdministrationWorkflowRow>({
                title: "Staatus",
                keyPath: "statusText",
                allowSorting: YES,
                weight: 1.6
            }),
            CBDataViewCellDescriptorWithObject<
                CompanyAdministrationWorkflowRow,
                CompanyWorkflowActionView
            >({
                title: "Tegevused",
                keyPath: "statusText",
                allowSorting: NO,
                weight: 0,
                minimumWidth: RHButtons.tableActionWidth * 2 + RHSpacing.small,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                initCustomView: () => new CompanyWorkflowActionView(),
                updateCell: (cellView, dataItem) => {
                    this._configureActionsView(cellView.customView, dataItem)
                }
            })
        ]
    }


    _configureActionsView(
        actionsView: CompanyWorkflowActionView,
        workflow: CompanyAdministrationWorkflowRow
    ) {
        actionsView.primaryButton.hidden = YES
        actionsView.secondaryButton.hidden = YES
        actionsView.primaryWasPressed = undefined
        actionsView.secondaryWasPressed = undefined

        if (workflow.action === "approveOrReject") {
            actionsView.primaryButton.hidden = NO
            actionsView.primaryButton.titleLabel.text = "Vaata"
            actionsView.primaryWasPressed = () => this._openWorkflow(workflow)
        }
        else if (workflow.action === "revoke") {
            actionsView.secondaryButton.hidden = NO
            actionsView.secondaryButton.titleLabel.text = "Tühista kutse"
            actionsView.secondaryWasPressed = () => {
                this._confirmAndRevokeInvitation(workflow)
            }
        }
        else if (workflow.action === "acceptOrReject") {
            actionsView.primaryButton.hidden = NO
            actionsView.secondaryButton.hidden = NO
            actionsView.primaryButton.titleLabel.text = "Võta vastu"
            actionsView.secondaryButton.titleLabel.text = "Lükka tagasi"
            actionsView.primaryWasPressed = () => {
                this._acceptInvitation(workflow)
            }
            actionsView.secondaryWasPressed = () => {
                this._rejectInvitation(workflow)
            }
        }
        else if (workflow.action === "cancel") {
            actionsView.secondaryButton.hidden = NO
            actionsView.secondaryButton.titleLabel.text = "Tühista"
            actionsView.secondaryWasPressed = () => {
                this._cancelMembershipRequest(workflow)
            }
        }
        actionsView.setNeedsLayout()
    }


    _openWorkflow(workflow: RHCompanyMembershipWorkflow) {
        const dialogView = new JoinRequestDialogView()
        dialogView.configureForWorkflow(workflow)
        dialogView.approveButton.controlEventTargetAccumulator.PrimaryActionTriggered = async () => {
            const approvedRole = dialogView.approvedRole
            if (!approvedRole) {
                return
            }
            dialogView.loading = YES
            try {
                const response = await SocketClient.ApproveCompanyMembershipRequest({
                    companyId: this.companyId,
                    requestId: workflow.requestId,
                    approvedRole
                })
                if (response.result?.success) {
                    dialogView.dialogView?.dismiss()
                    this.workflowsDidChange?.()
                }
                else {
                    this._showError(response, "Liitumistaotluse kinnitamine ebaõnnestus.")
                }
            }
            catch (error) {
                console.error("Approve company membership request failed:", error)
                CBDialogViewShower.alert("Liitumistaotluse kinnitamine ebaõnnestus.")
            }
            finally {
                dialogView.loading = NO
            }
        }
        dialogView.rejectButton.controlEventTargetAccumulator.PrimaryActionTriggered = async () => {
            dialogView.loading = YES
            try {
                const response = await SocketClient.RejectCompanyMembershipRequest({
                    companyId: this.companyId,
                    requestId: workflow.requestId
                })
                if (response.result?.success) {
                    dialogView.dialogView?.dismiss()
                    this.workflowsDidChange?.()
                }
                else {
                    this._showError(response, "Liitumistaotluse tagasilükkamine ebaõnnestus.")
                }
            }
            catch (error) {
                console.error("Reject company membership request failed:", error)
                CBDialogViewShower.alert("Liitumistaotluse tagasilükkamine ebaõnnestus.")
            }
            finally {
                dialogView.loading = NO
            }
        }
        CBDialogViewShower.showDialog(dialogView, undefined, NO)
    }


    async _acceptInvitation(workflow: RHCompanyMembershipWorkflow) {
        await this._performPersonalWorkflowAction(
            () => SocketClient.AcceptCompanyInvitation({ requestId: workflow.requestId }),
            "Kutse vastuvõtmine ebaõnnestus."
        )
    }


    async _rejectInvitation(workflow: RHCompanyMembershipWorkflow) {
        await this._performPersonalWorkflowAction(
            () => SocketClient.RejectCompanyInvitation({ requestId: workflow.requestId }),
            "Kutse tagasilükkamine ebaõnnestus."
        )
    }


    async _cancelMembershipRequest(workflow: RHCompanyMembershipWorkflow) {
        await this._performPersonalWorkflowAction(
            () => SocketClient.CancelCompanyMembershipRequest({
                requestId: workflow.requestId
            }),
            "Liitumistaotluse tühistamine ebaõnnestus."
        )
    }


    async _performPersonalWorkflowAction(
        action: () => Promise<{ errorResult?: unknown, result?: { success?: boolean } }>,
        fallback: string
    ) {
        try {
            const response = await action()
            if (response.result?.success) {
                this.workflowsDidChange?.()
            }
            else {
                this._showError(response, fallback)
            }
        }
        catch (error) {
            console.error("Company membership workflow action failed:", error)
            CBDialogViewShower.alert(fallback)
        }
    }


    _confirmAndRevokeInvitation(workflow: RHCompanyMembershipWorkflow) {
        const name = workflow.userName || workflow.userEmail || "kasutajale"
        const shower = CBDialogViewShower.showQuestionDialog(
            `Kas tühistad kasutajale ${name} saadetud kutse?`
        )
        shower.yesButtonWasPressed = async () => {
            shower.dialogView.dismiss()
            try {
                const response = await SocketClient.RevokeCompanyInvitation({
                    companyId: this.companyId,
                    requestId: workflow.requestId
                })
                if (response.result?.success) {
                    this.workflowsDidChange?.()
                }
                else {
                    this._showError(response, "Kutse tühistamine ebaõnnestus.")
                }
            }
            catch (error) {
                console.error("Revoke company invitation failed:", error)
                CBDialogViewShower.alert("Kutse tühistamine ebaõnnestus.")
            }
        }
    }


    _showError(response: { errorResult?: unknown }, fallback: string) {
        CBDialogViewShower.alert(
            RHCore.sharedInstance.errorMessageFromResponse(response, fallback) ?? fallback
        )
    }


    override layoutSubviews() {
        super.layoutSubviews()
        this.dataView.frame = this.contentBounds
    }


    override intrinsicContentHeight(constrainingWidth = 0) {
        return this.dataView.intrinsicContentHeight(constrainingWidth)
    }


    static _rows(
        companyWorkflows: RHCompanyMembershipWorkflow[],
        personalWorkflows: RHCompanyMembershipWorkflow[],
        roleText: (workflow: RHCompanyMembershipWorkflow) => string
    ): CompanyAdministrationWorkflowRow[] {
        const rowsById = new Map<string, CompanyAdministrationWorkflowRow>()
        for (const workflow of companyWorkflows) {
            rowsById.set(
                workflow.requestId,
                CompanyAdministrationJoinRequestsView._rowForWorkflow(workflow, "company", roleText)
            )
        }
        for (const workflow of personalWorkflows) {
            rowsById.set(
                workflow.requestId,
                CompanyAdministrationJoinRequestsView._rowForWorkflow(workflow, "personal", roleText)
            )
        }
        return [...rowsById.values()].sort(
            (first, second) =>
                new Date(second.createdAt).getTime() - new Date(first.createdAt).getTime()
        )
    }


    static _actionForWorkflow(
        workflow: Pick<RHCompanyMembershipWorkflow, "direction" | "status">,
        scope: CompanyAdministrationWorkflowScope
    ): CompanyAdministrationWorkflowAction {
        if (workflow.status !== "pending") {
            return "none"
        }
        if (scope === "company") {
            return workflow.direction === "user_to_company"
                ? "approveOrReject"
                : "revoke"
        }
        return workflow.direction === "company_to_user"
            ? "acceptOrReject"
            : "cancel"
    }


    static _rowForWorkflow(
        workflow: RHCompanyMembershipWorkflow,
        scope: CompanyAdministrationWorkflowScope,
        roleText: (workflow: RHCompanyMembershipWorkflow) => string
    ): CompanyAdministrationWorkflowRow {
        const identityPresentation = RHCompanyMemberPresentation.identityPresentationForPerson({
            name: workflow.userName,
            email: workflow.userEmail
        })
        const subjectText = scope === "company"
            ? identityPresentation.text || "Kasutaja"
            : workflow.company.name
        const subjectSearchText = scope === "company"
            ? identityPresentation.searchText
            : `${workflow.company.name} ${workflow.company.businessRegistryCode}`
        let typeText = CompanyAffiliationViewController.workflowTypeText(workflow)
        if (scope === "company") {
            typeText = workflow.direction === "user_to_company"
                ? "Liitumistaotlus"
                : "Saadetud kutse"
        }
        const statusText = scope === "company" && workflow.status === "pending"
            ? "Ootel"
            : CompanyAffiliationViewController.workflowStatusText(workflow)
        return {
            ...workflow,
            scope,
            subjectSearchText,
            subjectText,
            roleSearchText: roleText(workflow),
            statusText,
            typeText,
            action: CompanyAdministrationJoinRequestsView._actionForWorkflow(workflow, scope)
        }
    }

}
